/**
 * EditPanel Component
 *
 * A modal form for creating and editing assets or liabilities.
 * Handles validation, auto-history tracking, and CRUD operations.
 *
 * Props:
 *   - item: The asset/liability object to edit (or empty for new)
 *   - type: 'Assets' or 'Liabilities'
 *   - isNew: Boolean indicating if creating new item
 *   - onCancelHandler: Function to close the modal
 */
import 'styles/panel/edit.panel.css'
import { useState, useEffect } from 'react';
import { XIcon, TrashSimpleIcon } from '@phosphor-icons/react';
import AlertModal from 'components/modal/alert.modal';
import ClassificationCard from 'components/edit/cards/classification.card';
import DetailsCard from 'components/edit/cards/details.card';
import FinancialsCard from 'components/edit/cards/financials.card';
import ForecastCard from 'components/edit/cards/forecast.card';
import HistoryCard from 'components/edit/cards/history.card';
import { useData } from 'context/data.context';
import useEditValidation from 'hooks/useEditValidation';


const EditPanel = (props) => {

    // =========================================================================
    // CONTEXT & STATE
    // =========================================================================

    const {
        createAsset, updateAsset, deleteAsset,
        createLiability, updateLiability, deleteLiability,
        fetchHistoryForItem
    } = useData();

    const { validationErrors, historyValidationError, validateItem, validateHistoryEntry, clearErrors, clearHistoryError } = useEditValidation();

    const [item, setItem] = useState({ ...props.item });
    const [changed, setChanged] = useState(false);
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    // =========================================================================
    // DERIVED VALUES
    // =========================================================================

    const isNew = props.isNew;
    const isAsset = props.isAsset;
    const isLiability = !props.isAsset;
    const editClass = isAsset ? 'Asset' : 'Liability';

    const title = isNew
        ? `New ${isAsset ? 'Asset' : 'Liability'}`
        : `Edit ${isAsset ? 'Asset' : 'Liability'}`;

    const saveButtonText = isNew
        ? `Create ${isAsset ? 'Asset' : 'Liability'}`
        : `Update ${isAsset ? 'Asset' : 'Liability'}`;

    // =========================================================================
    // HANDLERS
    // =========================================================================

    const updateField = (field, value) => {
        if (item[field] === value) return;
        setItem({ ...item, [field]: value });
        setChanged(true);
    };

    const handleHistoryChange = (newHistory) => {
        setItem(prev => ({ ...prev, history: newHistory }));
        setChanged(true);
    };

    const handleSaveClick = async () => {
        if (!changed && !isNew) return;
        if (!validateItem(item)) return;
        clearErrors();

        let itemToSave = { ...item };

        // =====================================================================
        // HISTORY RECORDING LOGIC
        // =====================================================================

        const today = new Date().toISOString().split('T')[0];
        let history = [...(item.history || [])];

        // Scenario 1: New item - record today's date and valuation as first and only history
        if (isNew) {
            history = [{ date: today, valuation: item.valuation }];
            itemToSave.history = history;
        }

        // Scenario 2: Existing item, valuation changed - record as first entry, erase any today entries
        else if (props.item.valuation !== item.valuation) {
            history = history.filter(r => r.date !== today);
            history.unshift({ date: today, valuation: item.valuation });
            itemToSave.history = history;
        }

        // Scenarios 3-5: Existing item, valuation NOT changed
        else {
            const mostRecent = history[0];

            // Scenario 3: Most recent history valuation matches current - do nothing
            if (mostRecent && mostRecent.valuation === item.valuation) {
                // No changes needed
            }

            // Scenario 4: Most recent valuation differs AND most recent date is today - update it
            else if (mostRecent && mostRecent.date === today && mostRecent.valuation !== item.valuation) {
                history[0].valuation = item.valuation;
                itemToSave.history = history;
            }

            // Scenario 5: Most recent valuation differs AND most recent date is NOT today - create new entry
            else if (!mostRecent || (mostRecent.date !== today && mostRecent.valuation !== item.valuation)) {
                history.unshift({ date: today, valuation: item.valuation });
                itemToSave.history = history;
            }
        }

        // Call appropriate CRUD function based on type and mode
        setSaving(true);
        setSaveError(null);
        let result;
        if (isAsset && isNew) result = await createAsset(itemToSave);
        else if (isAsset && !isNew) result = await updateAsset(itemToSave, item.id);
        else if (isLiability && isNew) result = await createLiability(itemToSave);
        else result = await updateLiability(item.id, itemToSave);
        setSaving(false);

        if (result?.error) { setSaveError('Something went wrong. Please try again.'); return; }
        props.onCancelHandler();
    };

    const handleDeleteClick = () => {
        setShowDeleteAlert(true);
    };

    const confirmDelete = async () => {
        setSaving(true);
        const result = isAsset ? await deleteAsset(item.id) : await deleteLiability(item.id);
        setSaving(false);
        setShowDeleteAlert(false);
        if (result?.error) { setSaveError('Delete failed. Please try again.'); return; }
        props.onCancelHandler();
    };

    const cancelDelete = () => {
        setShowDeleteAlert(false);
    };

    // =========================================================================
    // EFFECTS
    // =========================================================================

    useEffect(() => {
        const loadHistory = async () => {
            if (!isNew && (!props.item.history || props.item.history.length === 0)) {
                setHistoryLoading(true);
                const history = await fetchHistoryForItem(props.item.id, isAsset ? 'Assets' : 'Liabilities');
                setItem(prev => ({ ...prev, history }));
                setHistoryLoading(false);
            }
        };
        loadHistory();
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                props.onCancelHandler();
            } else if (e.key === 'Enter') {
                e.preventDefault();
                handleSaveClick();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [item, changed]);

    // =========================================================================
    // RENDER
    // =========================================================================

    return (
      <>
        <div className={`Editing ${editClass} light`}>
      
            {/* HEADER: Close, Title, Delete */}
            <div className="flex row space center">
                    
              <p className="large">{title}</p>

        <div className='flex row center medium-gap'>
                    {!isNew && ( <div className='icon medium'> <TrashSimpleIcon onClick={handleDeleteClick} /> </div> ) }
                  <div className='icon medium'> <XIcon onClick={() => props.onCancelHandler()} /> </div>

                  </div>

            </div>

            <div className="flex col large-gap margin-top-large">
              <ClassificationCard
                classification={item.classification}
                isAsset={isAsset}
                isLiability={isLiability}
                validationError={validationErrors.classification}
                onFieldChange={updateField}
              />

              <DetailsCard
                item={item}
                isAsset={isAsset}
                isLiability={isLiability}
                validationError={validationErrors.name}
                onFieldChange={updateField}
              />

              <FinancialsCard
                item={item}
                isAsset={isAsset}
                isLiability={isLiability}
                validationError={validationErrors.valuation}
                onFieldChange={updateField}
              />

            
                <ForecastCard
                  item={item}
                  isAsset={isAsset}
                  isLiability={isLiability}
                  onFieldChange={updateField}
                />
       

    
                <HistoryCard
                  isAsset={isAsset}
                  isLiability={isLiability}
                  history={item.history}
                  historyLoading={historyLoading}
                  historyValidationError={historyValidationError}
                  validateHistoryEntry={validateHistoryEntry}
                  clearHistoryError={clearHistoryError}
                  onHistoryChange={handleHistoryChange}
                />
          
            </div>

            {/* ACTION BUTTONS */}
            <div className="flex col regular-gap margin-top-large">
              {saveError && <p className="form-error">{saveError}</p>}
              <button className="Save button large" disabled={(!changed && !isNew) || saving} onClick={handleSaveClick}>
                {saving ? 'Saving...' : saveButtonText}
              </button>
              <button className="Cancel button large" disabled={saving} onClick={() => props.onCancelHandler()}>
                Cancel
              </button>
            </div>
          </div>



        {/* DELETE CONFIRMATION MODAL */}
        {showDeleteAlert && <AlertModal title={`Delete ${isAsset ? "Asset" : "Liability"}?`} message="You will lose all its history and data." confirmText="Delete" onConfirm={confirmDelete} cancelText="Keep" onCancel={cancelDelete} />}
      </>
    );
};

export default EditPanel;
